/**
 * MarginBadge Component
 *
 * Displays a colour-coded badge for margin percentages.
 * - Green (emerald): High margin (>40%)
 * - Amber: Medium margin (20-40%)
 * - Red: Low margin (<20%)
 * - Gray: N/A (null/undefined)
 *
 * Part of US-P7-002 Step 6: UI Components
 */

/**
 * Margin threshold constants
 * Exported for use in tests and other components
 */
export const MARGIN_THRESHOLDS = {
  HIGH: 40,
  MEDIUM: 20
}

/**
 * Get the colour classes based on margin percentage
 *
 * @param {number|null} margin - Margin percentage
 * @returns {string} Tailwind colour classes
 */
function getMarginColour(margin) {
  if (margin === null || margin === undefined) {
    return 'bg-gray-100 text-gray-600'
  }
  if (margin > MARGIN_THRESHOLDS.HIGH) {
    return 'bg-emerald-100 text-emerald-700'
  }
  if (margin >= MARGIN_THRESHOLDS.MEDIUM) {
    return 'bg-amber-100 text-amber-700'
  }
  return 'bg-red-100 text-red-700'
}

/**
 * Get size classes based on size prop
 *
 * @param {string} size - Size variant
 * @returns {string} Tailwind size classes
 */
function getSizeClasses(size) {
  if (size === 'compact') {
    return 'text-xs px-1.5 py-0.5'
  }
  return 'text-sm px-2 py-1'
}

/**
 * MarginBadge Component
 *
 * @param {Object} props
 * @param {number|null} props.margin - Margin percentage to display
 * @param {string} [props.size='default'] - Size variant ('default' | 'compact')
 * @param {string} [props.className] - Additional CSS classes
 */
export default function MarginBadge({
  margin,
  size = 'default',
  className = ''
}) {
  const colourClasses = getMarginColour(margin)
  const sizeClasses = getSizeClasses(size)

  // Format the margin value
  const displayValue = margin === null || margin === undefined
    ? 'N/A'
    : `${margin.toFixed(1)}%`

  return (
    <span
      className={`inline-flex items-center font-medium rounded-full ${colourClasses} ${sizeClasses} ${className}`}
    >
      {displayValue}
    </span>
  )
}
